import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "sonner";
import { useHistory } from "react-router-dom";
import { socketConnection } from "../../services/socket";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Chip from "@material-ui/core/Chip";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import useTableStyles from "../../components/TableStyles";
import SearchIcon from "@material-ui/icons/Search";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CompanyModal from "../../components/CompaniesModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useDate } from "../../hooks/useDate";
import moment from "moment";

const reducer = (state, action) => {
    if (action.type === "LOAD_COMPANIES") {
        const companies = action.payload;
        const newCompanies = [];

        companies.forEach((company) => {
            const companyIndex = state.findIndex((u) => u.id === company.id);
            if (companyIndex !== -1) {
                state[companyIndex] = company;
            } else {
                newCompanies.push(company);
            }
        });

        return [...state, ...newCompanies];
    }

    if (action.type === "UPDATE_COMPANIES") {
        const company = action.payload;
        const companyIndex = state.findIndex((u) => u.id === company.id);

        if (companyIndex !== -1) {
            state[companyIndex] = company;
            return [...state];
        } else {
            return [company, ...state];
        }
    }

    if (action.type === "DELETE_COMPANIES") {
        const companyId = action.payload;

        const companyIndex = state.findIndex((u) => u.id === companyId);
        if (companyIndex !== -1) {
            state.splice(companyIndex, 1);
        }
        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(2, 3),
        borderRadius: 14,
        border: theme.palette.type === "dark"
            ? "1px solid rgba(255,255,255,0.06)"
            : "1px solid #E2E8F0",
        boxShadow: "none",
        backgroundColor: theme.palette.background.paper,
        overflowY: "auto",
        ...theme.scrollbarStyles,
    },
    searchField: {
        "& .MuiOutlinedInput-root": {
            borderRadius: 10,
            fontFamily: '"Inter", "Poppins", sans-serif',
            "& fieldset": {
                borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "#E2E8F0",
            },
            "&:hover fieldset": {
                borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.2)" : "#CBD5E1",
            },
            "&.Mui-focused fieldset": {
                borderColor: "#0B3C84",
            },
        },
    },
    statusChip: {
        fontFamily: '"Inter", "Poppins", sans-serif',
        fontSize: 12,
        fontWeight: 600,
        height: 24,
        borderRadius: 6,
        "& .MuiChip-label": {
            padding: "0 8px",
        },
    },
    statusActive: {
        backgroundColor: "rgba(34, 197, 94, 0.1)",
        color: "#16A34A",
    },
    statusInactive: {
        backgroundColor: "rgba(239, 68, 68, 0.1)",
        color: "#DC2626",
    },
    statusExpired: {
        backgroundColor: "rgba(245, 158, 11, 0.1)",
        color: "#D97706",
    },
}));

const Companies = () => {
    const classes = useStyles();
    const theme = useTheme();
    const tableClasses = useTableStyles();
    const history = useHistory();

    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [deletingCompany, setDeletingCompany] = useState(null);
    const [companyModalOpen, setCompanyModalOpen] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [searchParam, setSearchParam] = useState("");
    const [companies, dispatch] = useReducer(reducer, []);
    const { dateToClient, datetimeToClient } = useDate();

    // const { getPlanCompany } = usePlans();
    const { user } = useContext(AuthContext);

    useEffect(() => {
        async function fetchData() {
            if (!user.super) {
                toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
                setTimeout(() => {
                    history.push(`/`)
                }, 1000);
            }
        }
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [searchParam]);

    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            const fetchCompanies = async () => {
                try {
                    const { data } = await api.get("/companiesPlan/", {
                        params: { searchParam, pageNumber },
                    });
                    dispatch({ type: "LOAD_COMPANIES", payload: data.companies });
                    setHasMore(data.hasMore);
                    setLoading(false);
                } catch (err) {
                    toastError(err);
                }
            };
            fetchCompanies();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchParam, pageNumber]);

    useEffect(() => {
        const companyId = user.companyId;
        const socket = socketConnection({ companyId, userId: user.id });
        // const socket = socketConnection();

        return () => {
            socket.disconnect();
        };
    }, []);

    const handleOpenCompanyModal = () => {
        setSelectedCompany(null);
        setCompanyModalOpen(true);
    };

    const handleCloseCompanyModal = () => {
        setSelectedCompany(null);
        setCompanyModalOpen(false);
    };

    const handleSearch = (event) => {
        setSearchParam(event.target.value.toLowerCase());
    };

    const handleEditCompany = (company) => {
        setSelectedCompany(company);
        setCompanyModalOpen(true);
    };

    const handleDeleteCompany = async (companyId) => {
        try {
            await api.delete(`/companies/${companyId}`);
            toast.success(i18n.t("compaies.toasts.deleted"));
        } catch (err) {
            toastError(err);
        }
        setDeletingCompany(null);
        setSearchParam("");
        setPageNumber(1);
    };

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    const renderStatus = (row) => {
        // Verificar se a data de vencimento está vencida
        const isExpired = moment(row.dueDate).isValid() && moment(row.dueDate).diff(moment(), "days") <= 0;
        
        if (row.status === false) {
            return (
                <Chip
                    label="Inativo"
                    size="small"
                    className={`${classes.statusChip} ${classes.statusInactive}`}
                />
            );
        }
        
        if (isExpired) {
            return (
                <Chip
                    label="Vencido"
                    size="small"
                    className={`${classes.statusChip} ${classes.statusExpired}`}
                />
            );
        }
        
        return (
            <Chip
                label="Ativo"
                size="small"
                className={`${classes.statusChip} ${classes.statusActive}`}
            />
        );
    };

    const renderPlanValue = (row) => {
        return row.planId !== null ? row.plan.amount ? row.plan.amount.toLocaleString('pt-br', { minimumFractionDigits: 2 }) : '00.00' : "-";
    };

    const renderWhatsapp = (row) => {
        return row.useWhatsapp === false ? "Não" : "Sim";
    };

    const renderFacebook = (row) => {
        return row.useFacebook === false ? "Não" : "Sim";
    };

    const renderInstagram = (row) => {
        return row.useInstagram === false ? "Não" : "Sim";
    };

    const renderCampaigns = (row) => {
        return row.useCampaigns === false ? "Não" : "Sim";
    };

    const renderSchedules = (row) => {
        return row.useSchedules === false ? "Não" : "Sim";
    };

    const renderInternalChat = (row) => {
        return row.useInternalChat === false ? "Não" : "Sim";
    };

    const renderExternalApi = (row) => {
        return row.useExternalApi === false ? "Não" : "Sim";
    };

    const rowStyle = (record) => {
        // Não aplicar estilo de linha se a empresa estiver bloqueada (status === false)
        if (record.status === false) {
            return {};
        }
        
        if (moment(record.dueDate).isValid()) {
            const now = moment();
            const dueDate = moment(record.dueDate);
            const diff = dueDate.diff(now, "days");
            if (diff >= 1 && diff <= 5) {
                return { backgroundColor: "#fffead" };
            }
            // Removido o estilo vermelho para linhas vencidas - agora só mostra o chip "Vencido"
        }
        return {};
    };

    return (
        <MainContainer>
            <ConfirmationModal
                title={
                    deletingCompany &&
                    `${i18n.t("compaies.confirmationModal.deleteTitle")} ${deletingCompany.name}?`
                }
                open={confirmModalOpen}
                onClose={setConfirmModalOpen}
                onConfirm={() => handleDeleteCompany(deletingCompany.id)}
            >
                {i18n.t("compaies.confirmationModal.deleteMessage")}
            </ConfirmationModal>
            <CompanyModal
                open={companyModalOpen}
                onClose={handleCloseCompanyModal}
                aria-labelledby="form-dialog-title"
                companyId={selectedCompany && selectedCompany.id}
            />
            <MainHeader>
                <Title>{i18n.t("compaies.title")} ({companies.length})</Title>
                <MainHeaderButtonsWrapper>
                    <TextField
                        placeholder="Buscar empresa..."
                        type="search"
                        value={searchParam}
                        onChange={handleSearch}
                        variant="outlined"
                        size="small"
                        className={classes.searchField}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon 
                                        style={{ 
                                            color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
                                            fontSize: 20
                                        }} 
                                    />
                                </InputAdornment>
                            ),
                        }}
                    />
                </MainHeaderButtonsWrapper>
            </MainHeader>
            <Paper
                className={classes.mainPaper}
                elevation={0}
                onScroll={handleScroll}
            >
                <Table size="small">
                    <TableHead className={tableClasses.tableHead}>
                        <TableRow>
                            <TableCell className={tableClasses.tableHeadCell} align="center" style={{ width: "1%" }}>#</TableCell>
                            <TableCell className={tableClasses.tableHeadCell} align="center">{i18n.t("compaies.table.status")}</TableCell>
                            <TableCell className={tableClasses.tableHeadCell} align="left">{i18n.t("compaies.table.name")}</TableCell>
                            <TableCell className={tableClasses.tableHeadCell} align="left">{i18n.t("compaies.table.email")}</TableCell>
                            <TableCell className={tableClasses.tableHeadCell} align="center">{i18n.t("compaies.table.namePlan")}</TableCell>
                            <TableCell className={tableClasses.tableHeadCell} align="center">{i18n.t("compaies.table.value")}</TableCell>
                            <TableCell className={tableClasses.tableHeadCell} align="center">{i18n.t("compaies.table.createdAt")}</TableCell>
                            <TableCell className={tableClasses.tableHeadCell} align="center">{i18n.t("compaies.table.dueDate")}</TableCell>
                            <TableCell className={tableClasses.tableHeadCell} align="center">{i18n.t("compaies.table.lastLogin")}</TableCell>
                            <TableCell className={tableClasses.tableHeadCell} align="center" style={{ width: "1%" }}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <>
                            {companies.map((company) => (
                                <TableRow className={tableClasses.tableRow} style={rowStyle(company)} key={company.id}>
                                    <TableCell className={tableClasses.tableCell} align="center">{company.id}</TableCell>
                                    <TableCell className={tableClasses.tableCell} align="center">{renderStatus(company)}</TableCell>
                                    <TableCell className={tableClasses.tableCell} align="left">{company.name}</TableCell>
                                    <TableCell className={tableClasses.tableCell} align="left">{company.email}</TableCell>
                                    <TableCell className={tableClasses.tableCell} align="center">{company.plan?.name || "-"}</TableCell>
                                    <TableCell className={tableClasses.tableCell} align="center">R$ {renderPlanValue(company)}</TableCell>
                                    <TableCell className={tableClasses.tableCell} align="center">{dateToClient(company.createdAt)}</TableCell>
                                    <TableCell className={tableClasses.tableCell} align="center">{dateToClient(company.dueDate)}<br /><span>{company.recurrence}</span></TableCell>
                                    <TableCell className={tableClasses.tableCell} align="center">{datetimeToClient(company.lastLogin)}</TableCell>
                                    <TableCell className={tableClasses.tableCell} align="center">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEditCompany(company)}
                                            className={tableClasses.actionButton}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setConfirmModalOpen(true);
                                                setDeletingCompany(company);
                                            }}
                                            className={tableClasses.actionButton}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {loading && <TableRowSkeleton columns={10} />}
                        </>
                    </TableBody>
                </Table>
            </Paper>
        </MainContainer>
    );
};

export default Companies;
